import { isAxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useProjectActions } from './useProjectActions';
import { DEFAULT_DISPLAY_SETTINGS } from '../../types/types';
import type { DisplaySettings } from '../../types/types';
import type { FingeringSize } from '../../components/FingeringOverlay';
import type { PitchMode } from '../../model/pitchVisibility';
import { trackError } from '../../services/telemetry';

type BoolSetter = (next: boolean | ((prev: boolean) => boolean)) => void;

interface UseDisplaySettingsArgs {
  projectId: string | undefined;
  /**
   * The persisted settings as returned by the project poll. `null` means
   * "loaded, but the project has none yet" (legacy) and `undefined` means
   * "not loaded yet".
   */
  serverSettings: DisplaySettings | null | undefined;
  /**
   * Hydration gate. The hook adopts `serverSettings` (or the defaults) the
   * first time this flips true — by then the poll response has arrived, so
   * a `null` value reliably means "legacy project, use defaults" rather
   * than "still loading".
   */
  ready: boolean;
  /**
   * Whether toggle changes should be persisted. Guests pass `false`.
   */
  persist?: boolean;
  /** Display-settings token from the project. Omitted saves skip the check. */
  displayRevision?: number;
  /** Called with the token returned by a successful save. */
  onDisplayRevision?: (displayRevision: number) => void;
  /** Called after a save fails and its retries are used up. */
  onSaveError?: (message: string) => void;
}

/**
 * Display preferences for a project page.
 *
 * Hydrates once when `ready` turns true, then debounce-saves later
 * changes. A failed save is retried twice, then reported. The saved
 * value is recorded only after the request succeeds.
 */
export function useDisplaySettings({
  projectId,
  serverSettings,
  ready,
  persist = true,
  displayRevision,
  onDisplayRevision,
  onSaveError,
}: UseDisplaySettingsArgs) {
  // `useProjectActions` already no-ops every write when its arg is false, so
  // forwarding `persist` here is all that's needed to make guest toggle flips
  // local-only.
  const { updateDisplaySettings } = useProjectActions(persist);
  const [settings, setSettings] = useState<DisplaySettings>(
    DEFAULT_DISPLAY_SETTINGS
  );

  const hydratedRef = useRef(false);
  const lastSavedRef = useRef<DisplaySettings | null>(null);

  // `useProjectActions` returns a fresh `updateDisplaySettings` on every
  // render. Capturing it in a ref keeps it out of the debounce effect's
  // deps, so unrelated page re-renders (scroll, polling, edits) don't
  // restart the 500ms timer and starve the save.
  const updateRef = useRef(updateDisplaySettings);
  updateRef.current = updateDisplaySettings;
  const revisionRef = useRef(displayRevision);
  revisionRef.current = displayRevision;
  const onRevisionRef = useRef(onDisplayRevision);
  onRevisionRef.current = onDisplayRevision;
  const onSaveErrorRef = useRef(onSaveError);
  onSaveErrorRef.current = onSaveError;
  const attemptRef = useRef(0);
  const requestRef = useRef(0);
  const [retryNonce, setRetryNonce] = useState(0);

  // Hydrate once. By the time `ready` is true the poll response has
  // landed, so `serverSettings ?? defaults` correctly resolves legacy
  // (null) projects to the defaults.
  useEffect(() => {
    if (hydratedRef.current || !ready) return;
    const merged: DisplaySettings = {
      ...DEFAULT_DISPLAY_SETTINGS,
      ...(serverSettings ?? {}),
      pitchScope: serverSettings?.pitchScope === 'ledger' ? 'ledger' : 'all',
    };
    setSettings(merged);
    lastSavedRef.current = merged;
    hydratedRef.current = true;
  }, [ready, serverSettings]);

  // Reset hydration when switching projects so the next project re-adopts
  // its own persisted settings.
  useEffect(() => {
    hydratedRef.current = false;
    lastSavedRef.current = null;
    setSettings(DEFAULT_DISPLAY_SETTINGS);
  }, [projectId]);

  // Debounced persist. Skips the initial hydration value and any change
  // that matches what we last sent.
  useEffect(() => {
    if (!hydratedRef.current || !projectId) return;
    const last = lastSavedRef.current;
    if (
      last &&
      last.showAltFingerings === settings.showAltFingerings &&
      last.showAccidental === settings.showAccidental &&
      last.showPitch === settings.showPitch &&
      last.pitchScope === settings.pitchScope &&
      last.showFingerings === settings.showFingerings &&
      last.fontSize === settings.fontSize &&
      last.pitchFontSize === settings.pitchFontSize
    ) {
      return;
    }

    const handle = setTimeout(
      () => {
        const sent = settings;
        const requestId = ++requestRef.current;
        Promise.resolve(updateRef.current(projectId, sent, revisionRef.current))
          .then((saved) => {
            if (requestId !== requestRef.current) return;
            lastSavedRef.current = sent;
            attemptRef.current = 0;
            if (saved && typeof saved.displayRevision === 'number') {
              onRevisionRef.current?.(saved.displayRevision);
            }
          })
          .catch((err: unknown) => {
            if (requestId !== requestRef.current) return;
            const status = isAxiosError(err) ? err.response?.status : undefined;
            const errorObj =
              err instanceof Error ? err : new Error(String(err));
            trackError(errorObj, {
              source: 'useDisplaySettings',
              action: 'save',
              projectId,
              status,
            });
            const conflict = status === 409;
            if (!conflict && attemptRef.current < 2) {
              attemptRef.current += 1;
              setRetryNonce((nonce) => nonce + 1);
              return;
            }
            attemptRef.current = 0;
            onSaveErrorRef.current?.(
              conflict
                ? 'These display settings were changed somewhere else. Reload the page.'
                : 'Could not save display settings. Change them again to retry.'
            );
          });
      },
      attemptRef.current === 0 ? 500 : 1000
    );

    return () => clearTimeout(handle);
  }, [settings, projectId, retryNonce]);

  const setShowAltFingerings: BoolSetter = useCallback((next) => {
    setSettings((s) => ({
      ...s,
      showAltFingerings:
        typeof next === 'function' ? next(s.showAltFingerings) : next,
    }));
  }, []);

  const setShowAccidental: BoolSetter = useCallback((next) => {
    setSettings((s) => ({
      ...s,
      showAccidental:
        typeof next === 'function' ? next(s.showAccidental) : next,
    }));
  }, []);

  const setShowPitch: BoolSetter = useCallback((next) => {
    setSettings((s) => ({
      ...s,
      showPitch: typeof next === 'function' ? next(s.showPitch) : next,
    }));
  }, []);

  const setPitchMode = useCallback((mode: PitchMode) => {
    setSettings((s) => ({
      ...s,
      showPitch: mode !== 'off',
      pitchScope: mode === 'off' ? s.pitchScope : mode,
    }));
  }, []);

  const setShowFingerings: BoolSetter = useCallback((next) => {
    setSettings((s) => ({
      ...s,
      showFingerings:
        typeof next === 'function' ? next(s.showFingerings) : next,
    }));
  }, []);

  const setFontSize = useCallback((next: FingeringSize) => {
    setSettings((s) => ({ ...s, fontSize: next }));
  }, []);

  const setPitchFontSize = useCallback((next: FingeringSize) => {
    setSettings((s) => ({ ...s, pitchFontSize: next }));
  }, []);

  return {
    showAltFingerings: settings.showAltFingerings,
    showAccidental: settings.showAccidental,
    showPitch: settings.showPitch,
    pitchScope: settings.pitchScope,
    showFingerings: settings.showFingerings,
    fontSize: settings.fontSize,
    pitchFontSize: settings.pitchFontSize,
    setShowAltFingerings,
    setShowAccidental,
    setShowPitch,
    setPitchMode,
    setShowFingerings,
    setFontSize,
    setPitchFontSize,
  };
}
